import {
  type AdminSandboxUsage,
  formatSandboxRuntime,
  formatSandboxTransfer,
  SANDBOX_USAGE_WARNING_PERCENT,
  sandboxUsageRatio,
} from '@kyumo/common';
import { AlertTriangle, Container, HardDrive } from 'lucide-react';

interface SandboxUsageCardProps {
  /** 未計上・未設定の環境では undefined。その場合はカードを出さない */
  usage: AdminSandboxUsage | undefined;
  /**
   * どの範囲の使用量か。**同じ数字が別の意味を持つため、見出しで書き分ける。**
   * 上限判定は利用者別・全体の双方を見るので、どちらの話かが分からないと
   * 管理者は「なぜ止まっているのか」を取り違える。
   */
  scope?: 'system' | 'user';
  /**
   * 表示中の月が当月か。**現在形の断定を出してよいかの判定に使う。**
   *
   * この画面は過去の月も遡って見られるが、「確保は停止しています」は
   * **いまの状態**についての言明である。過去に上限へ達した月を開いただけで
   * これを出すと、当月には余裕があるのに管理者は確保が止まっていると読む。
   * 既定は真（月の切り替えを持たない画面から使われたときは当月の話とみなす）
   */
  isCurrentMonth?: boolean;
}

/**
 * 到達度（%）。
 *
 * **共有の `sandboxUsageRatio` をそのまま使う（丸めも頭打ちもしない）。**
 * ここで 100 に頭打ちすると、上限の 3 倍まで使った利用者について
 * 一覧（`UsersPage` の `sandboxPeakPercent`）は 312%、この画面は 100% と、
 * 同じ利用者の同じ月を画面ごとに違う数字で示すことになる。
 * 頭打ちが要るのは帯の幅だけであり、数字ではない
 */
const ratio = sandboxUsageRatio;

function UsageBar({ label, used, limit }: { label: string; used: string; limit: string }) {
  return (
    <div className="space-y-1">
      <div className="flex items-baseline justify-between text-sm">
        <span className="text-muted-foreground">{label}</span>
        <span className="text-foreground">
          {used}
          <span className="text-muted-foreground"> / {limit}</span>
        </span>
      </div>
    </div>
  );
}

/**
 * サンドボックス（インフラ費用）の使用量カード
 *
 * **言語モデルの利用料とは別の費目として並置する。** 合算すると、どちらの上限で
 * 止まったのかが分からなくなる。
 *
 * クラウド実行先を使っていない環境では計上そのものが無いため、カードを出さない。
 * 常に 0 のカードが並んでいると、他の数字まで読まれなくなる。
 */
export function SandboxUsageCard({
  usage,
  scope = 'system',
  isCurrentMonth = true,
}: SandboxUsageCardProps) {
  if (!usage) return null;

  const isUser = scope === 'user';

  const runtimeRatio = ratio(usage.runtimeSeconds, usage.limits.runtimeSeconds);
  const transferRatio = ratio(usage.transferBytes, usage.limits.transferBytes);
  const peak = Math.max(runtimeRatio, transferRatio);
  // 表示は切り捨てる。切り上げ・四捨五入だと、達していないのに 100% と掲げる
  const peakLabel = Math.floor(peak);

  return (
    <div className="bg-card rounded-xl p-6 border border-border">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-3 rounded-lg bg-blue-500/20 text-blue-400">
          <Container className="w-6 h-6" />
        </div>
        <div>
          <p className="text-foreground font-semibold">
            {isUser ? 'サンドボックス（この利用者）' : 'サンドボックス（全体）'}
            {/*
              **計上した行の月を数字の隣に出す。** 月の切り替えは JST で行うが、
              計上の行は UTC の月で積む。月初の 9 時間は選択中の月（例: 10 月）と
              ここに並ぶ数字（9 月）が食い違うため、見出しを持たないと管理者は
              前月の数字を当月として読む（サーバーはこの食い違いを伝えるために
              要求された月ではなく読んだ行の月を返している）
            */}
            <span className="ml-2 text-muted-foreground text-xs font-normal">{usage.month}</span>
          </p>
          <p className="text-muted-foreground text-sm">
            {isUser
              ? 'この利用者のクラウド実行環境の稼働時間と転送量（利用者ごとの上限に対する到達度）'
              : 'クラウド実行環境の稼働時間と転送量（言語モデルの利用料とは別費目）'}
          </p>
        </div>
      </div>

      <div className="space-y-3">
        <UsageBar
          label="稼働時間"
          used={formatSandboxRuntime(usage.runtimeSeconds)}
          limit={formatSandboxRuntime(usage.limits.runtimeSeconds)}
        />
        <UsageBar
          label="転送量"
          used={formatSandboxTransfer(usage.transferBytes)}
          limit={formatSandboxTransfer(usage.limits.transferBytes)}
        />

        <div className="h-2 rounded-full bg-muted overflow-hidden">
          {/* 上限に近いほうを出す。どちらか一方でも達すれば新規の確保は止まる */}
          <div
            className={
              peak >= 100
                ? 'h-full bg-destructive'
                : peak >= SANDBOX_USAGE_WARNING_PERCENT
                  ? 'h-full bg-amber-400'
                  : 'h-full bg-blue-400'
            }
            // 頭打ちするのは帯の幅だけ。数字（`peakLabel`）は実際の到達度を出す
            style={{ width: `${Math.min(100, peak)}%` }}
            role="progressbar"
            aria-label="サンドボックス使用量の上限到達度"
            // 支援技術へ渡す値は帯と同じ範囲に収める（`aria-valuemax` を超えない）
            aria-valuenow={Math.min(100, peakLabel)}
            aria-valuemin={0}
            aria-valuemax={100}
          />
        </div>

        {/*
          **確保の可否は当月の話としてだけ出す。** 数字そのものは過去の月でも
          正しいが、「確保できません」は現在形の言明である。過去の月を遡って
          見ているときに出すと、いまは余裕があるのに止まっていると読まれる
        */}
        {peak >= 100 && isCurrentMonth && (
          <p
            className="flex items-center gap-2 text-sm text-destructive"
            data-testid="sandbox-usage-exceeded"
          >
            <HardDrive className="w-4 h-4 shrink-0" />
            {isUser
              ? 'この利用者は上限に達しているため、新しい実行環境を確保できません'
              : '上限に達しているため、新しい実行環境の確保は停止しています'}
          </p>
        )}

        {/*
          **達する前に知らせる。** 上限に達してから気づくと、利用者は作業の途中で
          止まる。コスト上限と同じ段階（80%）で運用側へ先に見せる
        */}
        {peak >= SANDBOX_USAGE_WARNING_PERCENT && peak < 100 && isCurrentMonth && (
          <p
            className="flex items-center gap-2 text-sm text-amber-500"
            data-testid="sandbox-usage-warning"
          >
            <AlertTriangle className="w-4 h-4 shrink-0" />
            {isUser
              ? `この利用者は上限の ${peakLabel}% に達しています。超えると新しい実行環境を確保できなくなります`
              : `上限の ${peakLabel}% に達しています。超えると新しい実行環境の確保が止まります`}
          </p>
        )}
      </div>
    </div>
  );
}
